/**
 * @file extended-fab.tsx
 * MD3 Expressive Extended Floating Action Button.
 * @see https://m3.material.io/components/extended-fab/specs
 */

import { Slot } from "@radix-ui/react-slot";
import type { HTMLMotionProps } from "motion/react";
import { AnimatePresence, domMax, LazyMotion, m } from "motion/react";
import * as React from "react";
import { cn } from "../../../../lib/utils";
import { LoadingIndicator } from "../../../loading-indicator";
import { ProgressIndicator } from "../../../progress-indicator";
import { Ripple, useRippleState } from "../../../ripple";
import {
	ICON_SPAN_VARIANTS,
	SPRING_TRANSITION,
} from "../../../shared/constants";

const SIZE_HEIGHT: Record<string, string> = {
	sm: "h-14",
	md: "h-20",
	lg: "h-24",
};

const SIZE_WIDTH_COLLAPSED: Record<string, string> = {
	sm: "w-14",
	md: "w-20",
	lg: "w-24",
};

const SIZE_PADDING: Record<string, string> = {
	sm: "px-4",
	md: "px-[26px]",
	lg: "px-7",
};

const SIZE_GAP: Record<string, string> = {
	sm: "gap-2",
	md: "gap-3",
	lg: "gap-4",
};

const SIZE_ICON: Record<string, { cls: string; px: number }> = {
	sm: { cls: "size-6", px: 24 },
	md: { cls: "size-7", px: 28 },
	lg: { cls: "size-9", px: 36 },
};

const SIZE_TEXT: Record<string, string> = {
	sm: "text-base font-medium",
	md: "text-lg font-medium",
	lg: "text-xl font-normal",
};

const MORPH_RADIUS: Record<string, { default: number; pressed: number }> = {
	sm: { default: 16, pressed: 10 },
	md: { default: 20, pressed: 14 },
	lg: { default: 28, pressed: 18 },
};

const COLOR_CLASSES: Record<string, { bg: string; text: string }> = {
	primary: {
		bg: "bg-m3-primary-container",
		text: "text-m3-on-primary-container",
	},
	secondary: {
		bg: "bg-m3-secondary-container",
		text: "text-m3-on-secondary-container",
	},
	tertiary: {
		bg: "bg-m3-tertiary-container",
		text: "text-m3-on-tertiary-container",
	},
	surface: {
		bg: "bg-m3-surface-container-high",
		text: "text-m3-on-surface",
	},
};

type MotionButtonProps = Omit<HTMLMotionProps<"button">, "children" | "color">;

/** Props for the `ExtendedFAB` component. */
export interface ExtendedFABProps extends MotionButtonProps {
	/** Required text label. Keep it short (1-2 words); it does not wrap. */
	label: string;
	/** Icon to render. Automatically replaced by a spinner when `loading={true}`. */
	icon?: React.ReactNode;
	/** Button size per MD3 Expressive spec. @default "sm" */
	size?: "sm" | "md" | "lg";
	/** MD3 color role for the container surface. @default "primary" */
	colorStyle?: "primary" | "secondary" | "tertiary" | "surface";
	/** Reduces shadow to the lower elevation variant. @default false */
	lowered?: boolean;
	/** Replaces the icon with a spinner and blocks interaction. @default false */
	loading?: boolean;
	/** Spinner style shown while loading. @default "loading-indicator" */
	loadingVariant?: "loading-indicator" | "circular";
	/** Mounts/unmounts with an entrance/exit animation. @default true */
	visible?: boolean;
	/** Collapses to icon-only when the user scrolls down. @default false */
	collapseOnScroll?: boolean;
	/** Minimum scroll delta (px) required to trigger collapse/expand. @default 50 */
	scrollThreshold?: number;
	/** Controlled collapsed state. Overrides internal scroll-driven state when provided. */
	collapsed?: boolean;
	/** Callback fired when the internal collapsed state changes. */
	onCollapsedChange?: (collapsed: boolean) => void;
	/** Layout ID for Framer Motion Container Transform transitions. */
	layoutId?: string;
	/**
	 * Change the component to the underlying child element and merge props/behavior.
	 * Useful when wrapping in Next.js `<Link>` or custom anchor tags.
	 * @default false
	 */
	asChild?: boolean;
	children?: React.ReactNode;
}

/**
 * Internal hook that listens to scroll events and manages the collapsed state.
 * @internal
 */
function useScrollCollapse(
	enabled: boolean,
	scrollThreshold: number,
	onCollapsedChange?: (collapsed: boolean) => void,
) {
	const [collapsed, setCollapsed] = React.useState(false);
	const lastScrollY = React.useRef(0);

	React.useEffect(() => {
		if (!enabled || typeof window === "undefined") return;

		lastScrollY.current = window.scrollY;

		const handleScroll = () => {
			const currentScrollY = window.scrollY;
			const diff = currentScrollY - lastScrollY.current;

			if (Math.abs(diff) < scrollThreshold) return;

			if (diff > 0 && !collapsed) {
				setCollapsed(true);
				onCollapsedChange?.(true);
			} else if (diff < 0 && collapsed) {
				setCollapsed(false);
				onCollapsedChange?.(false);
			}

			lastScrollY.current = currentScrollY;
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [enabled, scrollThreshold, collapsed, onCollapsedChange]);

	return collapsed;
}

const ExtendedFABComponent = React.forwardRef<
	HTMLButtonElement,
	ExtendedFABProps
>(
	(
		{
			className,
			style,
			label,
			icon,
			size = "sm",
			colorStyle = "primary",
			lowered = false,
			loading = false,
			loadingVariant = "loading-indicator",
			visible = true,
			collapseOnScroll = false,
			scrollThreshold = 50,
			collapsed,
			onCollapsedChange,
			asChild = false,
			children,
			onClick,
			onKeyDown,
			layoutId,
			"aria-label": ariaLabel,
			...restProps
		},
		ref,
	) => {
		const localRef = React.useRef<HTMLButtonElement>(null);
		React.useImperativeHandle(ref, () => localRef.current as HTMLButtonElement);

		const [dir, setDir] = React.useState<"ltr" | "rtl">("ltr");

		React.useEffect(() => {
			if (!localRef.current) return;
			const closestDir = localRef.current.closest("[dir]")?.getAttribute("dir");
			if (closestDir === "rtl" || closestDir === "ltr") {
				setDir(closestDir);
			}
		}, []);

		const internalCollapsed = useScrollCollapse(
			collapseOnScroll,
			scrollThreshold,
			onCollapsedChange,
		);

		const isCollapsed = collapsed !== undefined ? collapsed : internalCollapsed;
		const hasIcon = !!icon || loading;

		const colors = COLOR_CLASSES[colorStyle] ?? COLOR_CLASSES.primary;
		const radiusConfig = MORPH_RADIUS[size] ?? MORPH_RADIUS.sm;
		const sizeIcon = SIZE_ICON[size] ?? SIZE_ICON.sm;

		const { ripples, onPointerDown, removeRipple } = useRippleState({
			disabled: loading,
		});

		const handleClick = React.useCallback(
			(e: React.MouseEvent<HTMLButtonElement>) => {
				if (loading) {
					e.preventDefault();
					return;
				}
				onClick?.(e);
			},
			[loading, onClick],
		);

		const handleKeyDown = React.useCallback(
			(e: React.KeyboardEvent<HTMLButtonElement>) => {
				if (loading) return;
				if ((e.key === "Enter" || e.key === " ") && onClick) {
					e.preventDefault();
					(e.currentTarget as HTMLButtonElement).click();
				}
				onKeyDown?.(e);
			},
			[loading, onClick, onKeyDown],
		);

		const containerClassName = cn(
			"relative shrink-0 inline-flex items-center select-none cursor-pointer overflow-hidden",
			"transition-[box-shadow,filter] duration-200",
			"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-m3-primary focus-visible:ring-offset-2",
			"disabled:pointer-events-none disabled:opacity-[0.38] disabled:shadow-none",
			colors.bg,
			colors.text,
			lowered ? "shadow-sm" : "shadow-md",
			SIZE_HEIGHT[size] ?? "h-14",
			SIZE_TEXT[size],
			isCollapsed
				? cn("px-0 justify-center", SIZE_WIDTH_COLLAPSED[size])
				: cn(
						hasIcon ? "justify-start" : "justify-center",
						SIZE_PADDING[size],
						hasIcon && SIZE_GAP[size],
					),
			dir === "rtl" || restProps.dir === "rtl"
				? "flex-row-reverse"
				: "flex-row",
			loading && "pointer-events-none opacity-75 cursor-not-allowed",
			className,
		);

		const innerContent = (
			<>
				<Ripple ripples={ripples} onRippleDone={removeRipple} />

				{hasIcon && (
					<m.span
						layout="position"
						className={cn(
							"flex items-center justify-center shrink-0",
							sizeIcon.cls,
						)}
					>
						<AnimatePresence mode="wait" initial={false}>
							{loading ? (
								<m.span
									key="loading"
									{...ICON_SPAN_VARIANTS}
									transition={SPRING_TRANSITION}
									className="flex items-center justify-center shrink-0 w-full h-full"
								>
									{loadingVariant === "loading-indicator" ? (
										<LoadingIndicator
											size={sizeIcon.px}
											color="currentColor"
											aria-label="Loading"
										/>
									) : (
										<ProgressIndicator
											variant="circular"
											size={sizeIcon.px}
											color="currentColor"
											trackColor="transparent"
											aria-label="Loading"
										/>
									)}
								</m.span>
							) : (
								icon && (
									<m.span
										key="icon"
										{...ICON_SPAN_VARIANTS}
										transition={SPRING_TRANSITION}
										aria-hidden="true"
										className="flex items-center justify-center shrink-0 w-full h-full [&>svg]:w-full [&>svg]:h-full"
									>
										{icon}
									</m.span>
								)
							)}
						</AnimatePresence>
					</m.span>
				)}

				<AnimatePresence initial={false}>
					{!isCollapsed && (
						<m.span
							key="label"
							layout="position"
							initial={{ opacity: 0, width: 0 }}
							animate={{ opacity: 1, width: "auto" }}
							exit={{ opacity: 0, width: 0 }}
							transition={{
								opacity: { duration: 0.15, ease: "linear" },
								width: SPRING_TRANSITION,
							}}
							className="overflow-hidden whitespace-nowrap"
						>
							{label}
						</m.span>
					)}
				</AnimatePresence>
			</>
		);

		return (
			<LazyMotion features={domMax} strict>
				<AnimatePresence>
					{visible &&
						!(isCollapsed && !hasIcon) &&
						(asChild ? (
							<Slot
								ref={localRef}
								aria-label={ariaLabel || label}
								aria-busy={loading || undefined}
								aria-disabled={loading || restProps.disabled}
								onClick={handleClick}
								onPointerDown={onPointerDown}
								onKeyDown={handleKeyDown}
								style={{
									...(style as React.CSSProperties),
									borderRadius: `${radiusConfig.default}px`,
								}}
								className={containerClassName}
								{...(restProps as React.HTMLAttributes<HTMLElement>)}
							>
								{React.cloneElement(
									React.Children.only(children) as React.ReactElement<{
										children?: React.ReactNode;
									}>,
									{ children: innerContent },
								)}
							</Slot>
						) : (
							<m.button
								ref={localRef}
								type="button"
								layout
								layoutId={layoutId}
								aria-label={ariaLabel || label}
								aria-busy={loading || undefined}
								aria-disabled={loading || restProps.disabled}
								onClick={handleClick}
								onPointerDown={onPointerDown}
								onKeyDown={handleKeyDown}
								style={style}
								initial={{
									scale: 0.6,
									opacity: 0,
									borderRadius: radiusConfig.default,
								}}
								animate={{
									scale: 1,
									opacity: 1,
									borderRadius: radiusConfig.default,
								}}
								exit={{ scale: 0.6, opacity: 0 }}
								whileTap={{ borderRadius: radiusConfig.pressed }}
								transition={{
									borderRadius: { type: "spring", stiffness: 500, damping: 30 },
									scale: SPRING_TRANSITION,
									opacity: { duration: 0.2, ease: "linear" },
									layout: SPRING_TRANSITION,
								}}
								className={containerClassName}
								{...restProps}
							>
								{innerContent}
							</m.button>
						))}
				</AnimatePresence>
			</LazyMotion>
		);
	},
);

ExtendedFABComponent.displayName = "ExtendedFAB";

export const ExtendedFAB = React.memo(ExtendedFABComponent);
